import { useEffect, useState } from "react";
import { MessageSquare, Loader2, TrendingUp, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const KIND_LABELS = {
  welcome: "Welcome",
  callback_nudge: "Callback nudge",
  decline_reminder: "Decline reminder",
  decline_followup: "Decline follow-up",
  customer_message: "Customer reply",
  thank_you: "Thank-you",
  ad_hoc: "Ad-hoc",
};

/**
 * Manager-dashboard widget — last-7-days SMS volume + estimated Twilio spend.
 * Self-fetches, gracefully degrades to a "no SMS sent yet" state.
 */
export default function SmsCostTile() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sms/stats?days=7")
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="card-default p-5 flex items-center gap-3" data-testid="sms-cost-tile">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading SMS costs…</span>
      </div>
    );
  }

  if (!stats) return null;

  const cost = (stats.cost_gbp || 0).toFixed(2);
  const sent = stats.sent || 0;
  const segments = stats.segments || 0;
  const topKinds = (stats.by_kind || []).slice(0, 4);

  return (
    <div className="card-default p-5" data-testid="sms-cost-tile">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" />
            <div className="label-overline">Last 7 days · SMS</div>
          </div>
          <div className="flex items-baseline gap-3 mt-2">
            <div className="font-display font-black text-3xl tracking-tight" data-testid="sms-cost-tile-sent">{sent}</div>
            <div className="text-xs text-muted-foreground">
              messages sent · <span className="font-mono-plex" data-testid="sms-cost-tile-segments">{segments}</span> billable segments
            </div>
          </div>
          <div className="mt-1 text-sm">
            <span className="text-muted-foreground">Estimated spend:</span>{" "}
            <span className="font-mono-plex font-bold" data-testid="sms-cost-tile-cost">£{cost}</span>
            <span className="text-[10px] text-muted-foreground/70 ml-1.5">@ £{(stats.rate_gbp_per_segment || 0.04).toFixed(2)}/seg</span>
          </div>
        </div>
        <Link
          to="/sms-usage"
          className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
          data-testid="sms-cost-tile-detail-link"
        >
          Details <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {topKinds.length > 0 && (
        <div className="mt-4 space-y-1.5">
          {topKinds.map((k) => (
            <div key={k.kind} className="flex items-center justify-between text-[11px]" data-testid={`sms-cost-tile-kind-${k.kind}`}>
              <span className="text-muted-foreground">{KIND_LABELS[k.kind] || k.kind}</span>
              <span className="font-mono-plex">
                <span className="text-foreground">{k.sent}</span>
                <span className="text-muted-foreground/60 ml-2">£{(k.cost_gbp || 0).toFixed(2)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {sent === 0 && (
        <p className="text-[11px] text-muted-foreground/80 mt-3 leading-relaxed">
          No SMS sent in the last 7 days. As soon as the team starts sending welcome / callback nudge / decline-follow-up texts, spend lands here automatically.
        </p>
      )}
    </div>
  );
}
